import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { Plus, MapPin, Clock, ChevronRight, Search, X } from "lucide-react";
import { toast } from "sonner";
import ProjectMap from "../components/ProjectMap";
import MapboxSearch from "../components/MapboxSearch";
import DatePickerAZ from "../components/DatePickerAZ";
import ConsultingTeamPicker from "../components/ConsultingTeamPicker";

export default function Projects() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [pick, setPick] = useState([]);
  const [query, setQuery] = useState("");
  const [team, setTeam] = useState({});
  const [form, setForm] = useState({
    name: "",
    length_km: "",
    description: "",
    deadline: "",
  });

  const canCreate = user?.role === "superadmin";

  const load = () => api.get("/projects").then((r) => setProjects(r.data));

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    try {
      const payload = {
        ...form,
        length_km: Number(form.length_km),
        consulting_team: team,
      };
      if (pick.length === 2 && pick[0]?.lng != null && pick[1]?.lng != null) {
        payload.start_lng = Number(pick[0].lng);
        payload.start_lat = Number(pick[0].lat);
        payload.end_lng = Number(pick[1].lng);
        payload.end_lat = Number(pick[1].lat);
      }
      await api.post("/projects", payload);
      toast.success("Layihə yaradıldı");
      setOpen(false);
      setPick([]);
      setTeam({});
      setForm({ name: "", length_km: "", description: "", deadline: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Xəta");
    }
  };

  // Update a single coordinate field (A=0, B=1, key='lng'|'lat')
  const updatePoint = (idx, key, val) => {
    const num = val === "" ? null : Number(val);
    setPick((prev) => {
      const next = [...prev];
      while (next.length <= idx) next.push({ lng: null, lat: null });
      next[idx] = { ...next[idx], [key]: num };
      return next;
    });
  };

  const filteredProjects = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter((p) =>
      (p.name || "").toLowerCase().includes(q) ||
      (p.description || "").toLowerCase().includes(q),
    );
  }, [projects, query]);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-overline mb-1">{t("projects")}</div>
          <h1 className="font-heading font-black text-4xl tracking-tight">
            Layihələr
          </h1>
        </div>
        {canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-[#004D99] hover:bg-[#003366]"
                data-testid="new-project-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> {t("new_project")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{t("new_project")}</DialogTitle>
              </DialogHeader>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <Label className="label-overline">{t("name")}</Label>
                    <Input
                      value={form.name}
                      onChange={(e) =>
                        setForm({ ...form, name: e.target.value })
                      }
                      data-testid="project-name"
                    />
                  </div>
                  <div>
                    <Label className="label-overline">{t("length_km")}</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={form.length_km}
                      onChange={(e) =>
                        setForm({ ...form, length_km: e.target.value })
                      }
                      data-testid="project-length"
                    />
                  </div>
                  <div>
                    <Label className="label-overline">{t("deadline")}</Label>
                    <DatePickerAZ
                      value={form.deadline}
                      onChange={(v) => setForm({ ...form, deadline: v })}
                      testid="project-deadline"
                      minDate={new Date()}
                    />
                  </div>
                  <div>
                    <Label className="label-overline">
                      {t("description")}
                    </Label>
                    <Input
                      value={form.description}
                      onChange={(e) =>
                        setForm({ ...form, description: e.target.value })
                      }
                    />
                  </div>

                  {/* Manual coordinate entry */}
                  <div className="border-t border-slate-200 pt-3 space-y-3">
                    <div className="label-overline">Yer axtarışı (autocomplete)</div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <div className="text-[10px] mono text-[#FF5722] font-bold mb-1">
                          A · BAŞLANĞIC
                        </div>
                        <MapboxSearch
                          testid="geocode-a"
                          placeholder="Başlanğıc yeri"
                          onSelect={(f) => {
                            updatePoint(0, "lng", f.lng);
                            updatePoint(0, "lat", f.lat);
                            toast.success(`A: ${f.text}`);
                          }}
                        />
                      </div>
                      <div>
                        <div className="text-[10px] mono text-[#004D99] font-bold mb-1">
                          B · SON
                        </div>
                        <MapboxSearch
                          testid="geocode-b"
                          placeholder="Son yeri"
                          onSelect={(f) => {
                            updatePoint(1, "lng", f.lng);
                            updatePoint(1, "lat", f.lat);
                            toast.success(`B: ${f.text}`);
                          }}
                        />
                      </div>
                    </div>

                    <div className="label-overline pt-1">Koordinatlar (manual)</div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[10px] mono text-[#FF5722] font-bold">
                          A · BAŞLANĞIC
                        </Label>
                        <div className="grid grid-cols-2 gap-1">
                          <Input
                            type="number"
                            step="0.0001"
                            placeholder="Lng"
                            value={pick[0]?.lng ?? ""}
                            onChange={(e) =>
                              updatePoint(0, "lng", e.target.value)
                            }
                            className="text-xs mono"
                            data-testid="coord-a-lng"
                          />
                          <Input
                            type="number"
                            step="0.0001"
                            placeholder="Lat"
                            value={pick[0]?.lat ?? ""}
                            onChange={(e) =>
                              updatePoint(0, "lat", e.target.value)
                            }
                            className="text-xs mono"
                            data-testid="coord-a-lat"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-[10px] mono text-[#FF5722] font-bold">
                          B · SON
                        </Label>
                        <div className="grid grid-cols-2 gap-1">
                          <Input
                            type="number"
                            step="0.0001"
                            placeholder="Lng"
                            value={pick[1]?.lng ?? ""}
                            onChange={(e) =>
                              updatePoint(1, "lng", e.target.value)
                            }
                            className="text-xs mono"
                            data-testid="coord-b-lng"
                          />
                          <Input
                            type="number"
                            step="0.0001"
                            placeholder="Lat"
                            value={pick[1]?.lat ?? ""}
                            onChange={(e) =>
                              updatePoint(1, "lat", e.target.value)
                            }
                            className="text-xs mono"
                            data-testid="coord-b-lat"
                          />
                        </div>
                      </div>
                    </div>
                    {pick.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setPick([])}
                        className="text-[10px] mono uppercase text-red-600 hover:underline"
                        data-testid="reset-coords"
                      >
                        Sıfırla
                      </button>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">
                    Yaxud xəritədən klikləyərək seçin (A → B)
                  </Label>
                  <ProjectMap
                    selectable
                    onPick={setPick}
                    initialPick={pick}
                    height={340}
                  />
                  {pick.length > 0 && (
                    <div className="mono text-[10px] text-slate-500 bg-slate-50 rounded p-2">
                      {pick.map((p, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span className={`w-4 h-4 rounded-full text-white text-[8px] flex items-center justify-center font-bold ${i === 0 ? "bg-[#FF5722]" : "bg-[#004D99]"}`}>
                            {i === 0 ? "A" : "B"}
                          </span>
                          {p?.lng != null ? p.lng.toFixed(4) : "—"}, {p?.lat != null ? p.lat.toFixed(4) : "—"}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="pt-4 border-t border-slate-200">
                <div className="label-overline mb-2">Konsulting komandası</div>
                <p className="text-xs text-slate-500 mb-3">
                  Yoxlanış zəncirinin 6 mərhələsi üçün hər rolu təyin edin. Komanda
                  tam olmasa, podratçı mərhələ yarada bilməyəcək və hissə təyin
                  oluna bilməyəcək.
                </p>
                <ConsultingTeamPicker
                  value={team}
                  onChange={setTeam}
                  testidPrefix="new-project-team"
                />
              </div>
              <DialogFooter>
                <Button
                  onClick={create}
                  className="bg-[#004D99] hover:bg-[#003366]"
                  data-testid="project-create-submit"
                >
                  {t("create")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Layihə adı və ya təsvir üzrə axtar..."
            className="pl-9 pr-9 bg-white"
            data-testid="projects-search"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              data-testid="projects-search-clear"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <span className="mono text-xs text-slate-500" data-testid="projects-count">
          {filteredProjects.length} / {projects.length} layihə
        </span>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {filteredProjects.length === 0 && (
          <div className="col-span-full text-center py-20 text-slate-500">
            {projects.length === 0 ? t("no_data") : "Axtarışa uyğun layihə tapılmadı"}
          </div>
        )}
        {filteredProjects.map((p) => (
          <Link
            key={p.id}
            to={`/projects/${p.id}`}
            className="bg-white border border-slate-200 rounded-lg p-5 hover:-translate-y-0.5 hover:shadow-md hover:border-[#004D99] transition-all group"
            data-testid={`project-card-${p.id}`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="label-overline">{p.length_km} km</div>
              <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-[#004D99]" />
            </div>
            <h3 className="font-heading font-bold text-lg mb-2 line-clamp-2">
              {p.name}
            </h3>
            <p className="text-sm text-slate-500 line-clamp-2 mb-4 min-h-[40px]">
              {p.description || "—"}
            </p>
            <div className="space-y-2">
              <div className="flex justify-between mono text-xs">
                <span className="text-slate-500">{t("completion")}</span>
                <span className="font-bold">{p.completion_pct}%</span>
              </div>
              <div className="h-1.5 bg-slate-100 rounded">
                <div
                  className="h-full bg-[#004D99] rounded"
                  style={{ width: `${p.completion_pct}%` }}
                />
              </div>
            </div>
            <div className="flex justify-between mt-4 pt-4 border-t border-slate-100 text-xs mono text-slate-500">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" /> {p.deadline}
              </span>
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {p.segments_count} hissə
              </span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
